import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import config from '@utils/env.config';
import {useTranslation} from 'react-i18next';
import {goBack, goTo} from '@utils';
import {useCountDown} from 'ahooks';
import _ from 'lodash';
import Page from '@businessComponents/games/page';
import {
  digitPickInfoQuery,
  getDigitResultV2,
  getQuickPickData,
  getQuickPickResult,
} from '@/common-pages/pick-digit/digit-service';
import {useRoute} from '@react-navigation/native';
import ResultTable, {digitCell} from '@/common-pages/result-v2/result-table';
import {ScrollView} from 'react-native';
import DigitBall from '@/common-pages/pick-digit/components/digit-ball';

const columns = [
  {
    title: 'Issue',
    key: 'pickNo',
    width: '40%',
    align: 'left' as const,
  },
  {
    title: 'Time',
    key: 'drawTime',
    align: 'center' as const,
    render(text: string, record: any) {
      if (record.drawTimeStr) {
        return record.drawTimeStr;
      }
      return text;
    },
  },
  {
    title: (
      <div className="flex gap-1 justify-end">
        {['A', 'B', 'C'].map((item, _i) => (
          <DigitBall ballSize={24} digit={item} color={item} key={_i} />
        ))}
      </div>
    ),
    key: 'drawResult',
    align: 'right' as const,
    render: (text: string) => {
      return digitCell(text);
    },
  },
];

function ResultDetailV2() {
  const params = useRoute().params as {
    id: number;
    type: 'quick' | 'digit';
    title: string;
    icon: string;
  };
  const {i18n} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState<{
    drawTime: string;
    drawTimeLong: number;
    currentPickNo?: string;
    cycle?: number;
  }>();
  const [initPage, setInitPage] = React.useState(1);
  const [total, setTotal] = useState(0);
  const pageConfig = useRef({
    pageNo: 1,
    pageSize: 10,
  });
  const [list, setList] = useState<any[]>([]);
  const nextTime = useMemo(() => {
    if (projectData?.drawTimeLong) {
      return projectData.drawTimeLong < 0
        ? projectData.drawTimeLong + 24 * 60 * 60 * 1000
        : projectData.drawTimeLong;
    }
    return 0;
  }, [projectData]);
  const [, formattedRes] = useCountDown({
    leftTime: nextTime,
    onEnd: () => {
      getProjectData();
      getResult(1);
    },
  });
  // 获取项目倒计时等数据
  const getProjectData = useCallback(() => {
    if (params.type === 'quick') {
      getQuickPickData().then(res => {
        // 找到匹配的
        const current = res.pickGames.find(
          item => item.pickThreeID == params.id,
        );
        if (current) {
          current.drawTimeLong = current.drawTimeLong * 1000 || 0;
          setProjectData(current as any);
        }
      });
    } else {
      digitPickInfoQuery(params.id + '').then(res => {
        // 过滤出已经开奖的
        const list = res.pickTimes.filter(item => item.drawTimeLong);
        if (list.length) {
          setProjectData(list[0]);
        }
      });
    }
  }, [params]);
  const getResult = async (pageNo: number) => {
    try {
      setInitPage(pageNo);
      setLoading(true);
      pageConfig.current.pageNo = pageNo;
      if (params.type === 'quick') {
        // getQuickPickResult
        const res = await getQuickPickResult({
          pageNo: pageNo,
          pageSize: pageConfig.current.pageSize,
          pickThreeId: +params.id,
        });
        setTotal(res.totalCount);
        setList(res.drawResultList as any);
      } else {
        const res = await getDigitResultV2({
          pageNo: pageConfig.current.pageNo,
          pageSize: pageConfig.current.pageSize,
          pickThreeId: +params.id,
        });
        setList(res.data.pickResultList as any);
        setTotal(res.data.totalCount);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };
  const CountDownCell = ({time}: {time: number}) => {
    return (
      <div className="flex items-center rounded-[2px] flex-1 justify-center text-t2 text-xs font-bold gap-1">
        {_.padStart(time + '', 2, '0')
          .split('')
          .map((item: string, index: number) => {
            return (
              <div
                key={index}
                className="rounded w-[1.1875rem] text-[1.25rem] bg-black px-1 flex items-center justify-center h-8 text-white">
                {item}
              </div>
            );
          })}
      </div>
    );
  };
  useEffect(() => {
    getProjectData();
    getResult(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const handleToPlay = () => {
    const isquick = params.type === 'quick';
    const qs: any = {
      id: params.id,
      pickName: params.title,
      pickGameType: isquick ? 'quick' : 'normal',
    };
    if (isquick) {
      qs.cycle = projectData?.cycle;
    }
    // cycle
    goTo('Digit', qs);
  };
  return (
    <div className="flex-1 flex flex-col min-h-0 h-0">
      <DetailNavTitle
        title={i18n.t('home.digit.title')}
        hideServer
        onBack={goBack}
        hideAmount={!!config.reactH5Url}
      />
      <ScrollView
        style={{
          flexBasis: 0,
          flexGrow: 1,
          minHeight: 0,
          maxHeight: '100%',
          paddingBottom: 56,
        }}>
        <div className="bg-gradient-to-b from-[#DCEBFF] to-[#B0D0FF] pt-3 px-3 pb-6">
          <div className="flex items-center gap-3">
            <img className="w-12 h-12" src={params.icon} alt={params.icon} />
            <div>
              <div className="text-t1 text-sm font-bold">{params.title}</div>
              <div className="text-t2 text-xs font-medium mt-1 ">
                Draw Result
              </div>
            </div>
            <div
              className="bg-[#166BE5] ml-auto text-white text-sm font-bold rounded-[20px] h-8 px-3 flex items-center justify-center w-max active:bg-[#0e4da7]"
              onClick={handleToPlay}>
              {i18n.t('bets-page.label.playNow')}
            </div>
          </div>
          <div className="bg-[rgba(0,0,0,0.4)] shadow-[inset_0px_4px_4px_0px_rgba(0,0,0,0.1)] flex justify-between items-center py-2 px-3 mt-2">
            <div>
              <div className="text-xs font-medium text-[#f4f5f8]">New Draw</div>
              <div className="text-white text-xs font-bold mt-1">
                {projectData?.currentPickNo || projectData?.drawTime}
              </div>
            </div>
            <div className="text-white text-xs font-bold mt-1">
              <div className="flex justify-between items-center gap-0.5 text-t2 font-bold">
                <CountDownCell time={formattedRes.hours} />
                <div className="text-t2 text-[1.25rem] font-bold">:</div>
                <CountDownCell time={formattedRes.minutes} />
                <div className="text-t2 text-[1.25rem] font-bold">:</div>
                <CountDownCell time={formattedRes.seconds} />
              </div>
            </div>
          </div>
        </div>
        <ResultTable
          key="pickNo"
          dataSource={list}
          columns={columns}
          loading={loading}
        />
      </ScrollView>
      {list.length > 0 ? (
        <div className="absolute bottom-0 left-0 right-0 h-fit bg-white">
          <Page
            page={initPage}
            total={total}
            style={{marginTop: 0}}
            pageSize={pageConfig.current.pageSize}
            onPageChange={p => {
              getResult(p);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

export default ResultDetailV2;
